import React, { useMemo } from 'react';
import DateTimePicker from '../../ListaDemandas/FiltroListaDemandas/DateTimePicker';
import { EditavelContainer, ViewModeText, EditModeContainer, ErrorMessage } from '../EditavelCommon/styles';
import { DateTimePickerWrapper } from './styles';

interface DateTimePickerEditavelProps {
    value: string | undefined;
    onChange: (value: string) => void;
    isEditing: boolean;
    label?: string;
    required?: boolean;
    error?: string;
    minDate?: Date;
    maxDate?: Date;
    timezone?: string;
    disabled?: boolean;
    onBlur?: () => void;
}

const DateTimePickerEditavel: React.FC<DateTimePickerEditavelProps> = ({
    value,
    onChange,
    isEditing,
    label,
    required = false,
    error,
    minDate,
    maxDate,
    timezone,
    disabled = false,
    onBlur,
}) => {
    const formatDateForDisplay = (dateString: string | undefined): string => {
        if (!dateString) return 'N/A';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            
            return `${day}/${month}/${year} às ${hours}h${minutes}`;
        } catch {
            return 'N/A';
        }
    };

    const formatDateForDateTimePicker = (dateString: string | undefined): string => {
        if (!dateString) return '';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            
            // O DateTimePicker aceita YYYY-MM-DD ou DD/MM/YYYY
            // Vamos usar YYYY-MM-DD que é mais simples e o componente extrai o horário do ISO string
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            
            // Retorna no formato que o DateTimePicker espera: YYYY-MM-DD HH:MM
            // Mas o componente parseDateFromValue só pega a data, então vamos usar YYYY-MM-DD
            // e o extractTimeFromValue vai pegar o horário do valor completo
            return `${year}-${month}-${day} ${hours}:${minutes}`;
        } catch {
            return '';
        }
    };

    const handleDateTimeChange = (date: string, time: string) => {
        if (!date) {
            onChange('');
            return;
        }

        try {
            const [year, month, day] = date.split('-');
            const [hours, minutes] = time.split(':');
            
            if (year && month && day && hours && minutes) {
                const dateObj = new Date(
                    parseInt(year),
                    parseInt(month) - 1,
                    parseInt(day),
                    parseInt(hours),
                    parseInt(minutes)
                );
                
                if (!isNaN(dateObj.getTime())) {
                    if (minDate && dateObj < minDate) {
                        return;
                    }
                    if (maxDate && dateObj > maxDate) {
                        return;
                    }
                    
                    const yearStr = String(dateObj.getFullYear());
                    const monthStr = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const dayStr = String(dateObj.getDate()).padStart(2, '0');
                    const hoursStr = String(dateObj.getHours()).padStart(2, '0');
                    const minutesStr = String(dateObj.getMinutes()).padStart(2, '0');
                    const secondsStr = String(dateObj.getSeconds()).padStart(2, '0');
                    
                    const newValue = `${yearStr}-${monthStr}-${dayStr}T${hoursStr}:${minutesStr}:${secondsStr}`;
                    onChange(newValue);
                }
            }
        } catch {
            // Ignora erros de parsing
        }
    };

    const displayValue = useMemo(() => {
        return formatDateForDisplay(value);
    }, [value]);

    const datePickerValue = useMemo(() => {
        return formatDateForDateTimePicker(value);
    }, [value]);

    // Se estiver desabilitado, sempre mostra modo visualização
    if (disabled || !isEditing) {
        return (
            <EditavelContainer>
                <ViewModeText>
                    {displayValue} {timezone ? `(${timezone})` : ''}
                </ViewModeText>
                {error && <ErrorMessage>{error}</ErrorMessage>}
            </EditavelContainer>
        );
    }

    return (
        <EditavelContainer>
            <EditModeContainer>
                <DateTimePickerWrapper>
                    <DateTimePicker
                        value={datePickerValue}
                        onChange={handleDateTimeChange}
                        onBlur={onBlur}
                        placeholder="Selecione a data e hora"
                        error={!!error}
                    />
                </DateTimePickerWrapper>
                {error && <ErrorMessage>{error}</ErrorMessage>}
            </EditModeContainer>
        </EditavelContainer>
    );
};

export default DateTimePickerEditavel;

